import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import {
    SimplePlaybookHandler,
    CustomPlaybookHandler,
    type PlaybookHandler,
} from '../playbook-handler';
import { generateUUID } from '@telecom/shared/utils/uuid';
import { IpProfileExternalService } from '../ip-profile-external-service';
import { MonitorAgentService } from '../monitor-agent-service';

// 引入 SVG 图标
import AINoveltySearchSvg from '@/presentation/assets/images/icons/ai_novelty_search.svg';
import AISpecificationSvg from '@/presentation/assets/images/icons/ai_specification.svg';
import AIFTOSearchSvg from '@/presentation/assets/images/icons/ai_fto_search.svg';
import AIPatentDisclosureSvg from '@/presentation/assets/images/icons/ai_patent_disclosure.svg';
import AITranslationSvg from '@/presentation/assets/images/icons/ai_translation.svg';
import AIImageSearchSvg from '@/presentation/assets/images/icons/ai_image_search.svg';
import ProtectSmartSearchSvg from '@/presentation/assets/images/icons/protect_smart_search.svg';
import type { PlaybookRegistry } from '../playbook-registry';

/**
 * Playbook 注册表实现
 * 管理所有 Playbook 的处理器
 */
@injectable()
export class PlaybookRegistryImpl implements PlaybookRegistry {
    private handlers: Map<string, PlaybookHandler> = new Map();

    constructor(
        @inject(IpProfileExternalService)
        private _ipProfileExternalService: IpProfileExternalService,
        @inject(MonitorAgentService)
        private _monitorAgentService: MonitorAgentService,
    ) {
        this.registerDefaultHandlers();
    }

    /**
     * 注册默认的 Playbook 处理器
     */
    // eslint-disable-next-line max-lines-per-function
    private registerDefaultHandlers() {
        // AI 新颖性检索
        this.register(
            'ai_novelty_search',
            new SimplePlaybookHandler(
                'ai_novelty_search',
                AINoveltySearchSvg,
                () => `/ip/novelty-check-report/${generateUUID()}`,
            ),
        );

        // AI 撰写说明书
        this.register(
            'ai_specification',
            new SimplePlaybookHandler(
                'ai_specification',
                AISpecificationSvg,
                () => `/ip/specification/${generateUUID()}`,
            ),
        );

        // AI FTO 检索
        this.register(
            'ai_fto_search',
            new SimplePlaybookHandler(
                'ai_fto_search',
                AIFTOSearchSvg,
                () => `/ip/fto-report/${generateUUID()}`,
            ),
        );

        // AI 专利交底书
        this.register(
            'ai_patent_disclosure',
            new SimplePlaybookHandler(
                'ai_patent_disclosure',
                AIPatentDisclosureSvg,
                () => `/ip/disclosure/${generateUUID()}`,
            ),
        );

        // AI 翻译
        this.register(
            'ai_translation',
            new SimplePlaybookHandler(
                'ai_translation',
                AITranslationSvg,
                () => `/ip/translation/${generateUUID()}`,
            ),
        );

        // AI 图像搜索
        this.register(
            'ai_image_search',
            new SimplePlaybookHandler(
                'ai_image_search',
                AIImageSearchSvg,
                () => '/ai-ip/image-fto-search',
            ),
        );

        // Monitor - Agent 类型，使用专门的服务处理
        this.register(
            'monitor',
            new CustomPlaybookHandler(
                'monitor',
                (params) => this._monitorAgentService.handleClick({
                    agentId: params.agentId,
                    teamId: params.teamId,
                }),
                AIImageSearchSvg,
            ),
        );

        // IP Profile External - 使用专门的服务处理
        this.register(
            'ip_profile_external',
            new CustomPlaybookHandler(
                'ip_profile_external',
                () => this._ipProfileExternalService.handleClick(),
                undefined, // 不显示图标
            ),
        );

        // 智能搜索
        this.register(
            'protect_smart_search',
            new SimplePlaybookHandler(
                'protect_smart_search',
                ProtectSmartSearchSvg,
                () => '/general-search',
            ),
        );
    }

    /**
     * 注册 Playbook 处理器
     */
    register(playbookId: string, handler: PlaybookHandler): void {
        this.handlers.set(playbookId, handler);
    }

    /**
     * 获取 Playbook 处理器
     */
    getHandler(playbookId: string): PlaybookHandler | undefined {
        return this.handlers.get(playbookId);
    }

    /**
     * 检查是否已注册
     */
    has(playbookId: string): boolean {
        return this.handlers.has(playbookId);
    }
}
